import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { FieldPath, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetDocumentConfigQuery, usePostDocumentsMutation } from "src/core/api/documents";
import { FolderFileDocumentCode } from "src/core/constants/folder";
import { useDocumentPreview } from "src/core/contexts/DocumentPreviewContext";
import { useFolder } from "src/core/contexts/FolderContext";
import {
  defaultMissionLetterValues,
  MissionLetterFormValues,
  missionLetterSchema,
} from "src/core/forms/missions/missionLetter.schema";
import { useDeleteDocumentMutation } from "src/core/gql/graphql";
import { useToast } from "src/core/hooks/use-toast";
import { uploadDocument } from "src/core/services/document.service";
import { validateDocument } from "src/core/utils/documentValidation";

export const useMissionLetter = () => {
  const { folderFile, refetch } = useFolder();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [postDocuments] = usePostDocumentsMutation();
  const [deleteDocument] = useDeleteDocumentMutation();
  const form = useForm<MissionLetterFormValues>({
    resolver: zodResolver(missionLetterSchema()),
    defaultValues: defaultMissionLetterValues(),
  });
  const { handlePreviewDocument } = useDocumentPreview();

  useEffect(() => {
    const missionLetter = folderFile.folderFile?.fileCustomerFolder?.stakeholders
      ?.find((stakeholder) => stakeholder.isFolderCustomer)
      ?.supportingDocuments?.find(
        (document) => document.abstractDocumentType.code === FolderFileDocumentCode.MISSION_LETTER
      )?.mainDocument;
    if (missionLetter && missionLetter.__typename === "Document") {
      const document = {
        file: null,
        existing: false,
        fileId: missionLetter.id,
        fileName: missionLetter.name ?? undefined,
        expirationDate: missionLetter.expirationDate ?? undefined,
      };
      form.reset({
        documents: {
          [FolderFileDocumentCode.MISSION_LETTER]: document,
        },
      });
    }
  }, [folderFile, form]);
  const { data: documentConfig } = useGetDocumentConfigQuery();

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, code: string) => {
      const file = event.target.files?.[0];
      if (file) {
        const validation = validateDocument(file, documentConfig);
        if (validation.isValid) {
          const entityId = folderFile.folderFile?.fileCustomerFolder?.stakeholders?.find(
            (stakeholder) => stakeholder.isFolderCustomer
          )?.entity?.id;
          if (entityId) {
            form.setValue(
              `documents.${code}.file` as FieldPath<MissionLetterFormValues>,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              file as any
            );
            setFileErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[code];
              return newErrors;
            });
            const currentValues = form.getValues();
            const doc = currentValues.documents[code];
            if (doc.file) {
              try {
                setIsLoading(true);
                await uploadDocument(doc, code, entityId, postDocuments);
                toast({
                  description: t("folder.missions.missionLetter.success"),
                });
              } catch (error) {
                console.error("useMissionLetter::handleFileChange", error);
                form.reset({
                  ...currentValues,
                  documents: {
                    ...currentValues.documents,
                    [code]: {
                      file: null,
                      existing: false,
                      fileId: undefined,
                      fileName: undefined,
                      expirationDate: undefined,
                    },
                  },
                });
                toast({
                  description: t("folder.missions.missionLetter.error"),
                  variant: "destructive",
                });
              } finally {
                setIsLoading(false);
              }
            }
            await refetch();
          }
        } else {
          setFileErrors((prev) => ({ ...prev, [code]: validation.error || "" }));
          event.target.value = "";
        }
      }
    },
    [folderFile, refetch, toast, t, form, postDocuments, documentConfig]
  );

  const deleteDoc = useCallback(
    async (docCode: string) => {
      try {
        setIsLoading(true);
        const fileId = form.getValues().documents[docCode].fileId;
        if (fileId) {
          await deleteDocument({
            variables: {
              documentId: fileId,
            },
          });
        }
        const currentValues = form.getValues();
        form.reset({
          ...currentValues,
          documents: {
            ...currentValues.documents,
            [docCode]: {
              file: null,
              existing: false,
              fileId: undefined,
              fileName: undefined,
              expirationDate: undefined,
            },
          },
        });
      } catch (error) {
        console.error("useMissionLetter::deleteDoc", error);
        toast({
          description: t("folder.missions.missionLetter.error"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [form, deleteDocument, toast, t]
  );

  return { form, handleFileChange, isLoading, deleteDoc, fileErrors, handlePreviewDocument };
};
